import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import {
  ceremonyCancelled,
  readAuthenticationCeremony,
  requestAuthorization,
} from './authorization';
import {
  authenticationBody,
  credentialBody,
  isCredential,
  postJSON,
  readCeremonyId,
  readCreationOptions,
  readRequestOptions,
  readSession,
  type PasskeySession,
} from './http';

export type { PasskeySession } from './http';

export type Registration = {
  continueRegistration?: () => Promise<void>;
};

const authorizationHeader = 'X-PassKey-Token';

async function browserCredential<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (ceremonyCancelled(error)) throw new Error('passkey_cancelled');
    throw error;
  }
}

async function creationCeremony(headers: Record<string, string> = {}) {
  const started = await postJSON('/passkey/register/start', {}, headers);
  const ceremonyId = readCeremonyId(started);
  const optionsJSON = readCreationOptions(started);
  if (!ceremonyId || !optionsJSON) throw new Error('passkey');
  const credential = await browserCredential(() =>
    startRegistration({
      optionsJSON: optionsJSON as unknown as PublicKeyCredentialCreationOptionsJSON,
    }),
  );
  if (!isCredential(credential)) throw new Error('passkey');
  await postJSON('/passkey/register/finish', credentialBody(ceremonyId, credential));
}

export async function authenticate(): Promise<PasskeySession> {
  const started = await postJSON('/auth/passkey/start', {});
  const ceremonyId = readCeremonyId(started);
  const optionsJSON = readRequestOptions(started);
  if (!ceremonyId || !optionsJSON) throw new Error('passkey');
  const credential = await browserCredential(() =>
    startAuthentication({
      optionsJSON: optionsJSON as unknown as PublicKeyCredentialRequestOptionsJSON,
    }),
  );
  if (!isCredential(credential)) throw new Error('passkey');
  const raw = await postJSON(
    '/auth/passkey/finish',
    authenticationBody(ceremonyId, credential),
  );
  const session = readSession(raw);
  if (!session) throw new Error('passkey');
  return session;
}

export async function register(): Promise<Registration> {
  try {
    await creationCeremony();
    return {};
  } catch (error) {
    const ceremony = readAuthenticationCeremony(errorRecord(error));
    if (!ceremony) throw error;
    const ceremonyId = await requestAuthorization(ceremony);
    return {
      continueRegistration: () => creationCeremony({ [authorizationHeader]: ceremonyId }),
    };
  }
}

function errorRecord(error: unknown): unknown {
  if (typeof error !== 'object' || error === null || !('params' in error)) return null;
  const params = (error as { params?: unknown }).params;
  const code = (error as { code?: unknown }).code;
  if (code !== 'webauthn_required') return null;
  return params;
}
